import { useMemo, useState, type Dispatch } from 'react';
import { DateTime } from 'luxon';
import { Download } from 'lucide-react';
import { AnimatedNumber } from '@/components/orders-drilldown/animated-number';
import {
  ReviewGrid,
  type ReviewGridRow,
} from '@/components/campaign-import/review-grid';
import {
  getImportCounts,
  type WizardAction,
  type WizardState,
} from '@/components/campaign-import/import-wizard.reducer';
import { useImportErrorMessages } from '@/components/campaign-import/use-import-error-messages';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useI18nContext } from '@/i18n/i18n-react';
import { downloadCsvFile } from '@/lib/campaign-import/download-csv-file';
import { buildErrorsCsv } from '@/lib/campaign-import/errors-csv';
import type { ImportRowValues } from '@/lib/campaign-import/row-validation';

export interface ReviewStepProps {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'good' | 'bad' | 'neutral';
}) {
  return (
    <div
      className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3"
      data-tone={tone}
    >
      <p className="text-xs text-white/60">{label}</p>
      <p
        className={
          tone === 'good'
            ? 'text-2xl font-semibold text-emerald-300'
            : tone === 'bad'
              ? 'text-2xl font-semibold text-red-300'
              : 'text-2xl font-semibold text-white'
        }
      >
        <AnimatedNumber value={value} format={(n) => String(Math.round(n))} />
      </p>
    </div>
  );
}

export function ReviewStep({ state, dispatch }: ReviewStepProps) {
  const { LL } = useI18nContext();
  const messages = useImportErrorMessages(state.storeWebsiteDomain);
  const [onlyErrors, setOnlyErrors] = useState(true);
  // Rows the user edited stay listed under the errors filter until it is switched off,
  // otherwise a row would vanish from under the cursor as soon as it turns valid.
  const [touched, setTouched] = useState<ReadonlySet<number>>(new Set());

  const { file, validation, edits, mapping } = state;
  const counts = getImportCounts(state);

  const gridRows = useMemo<ReviewGridRow[]>(() => {
    if (!file || !validation) return [];
    const cell = (index: number, field: keyof ImportRowValues): string => {
      const edited = edits[index]?.[field];
      if (edited !== undefined) return edited;
      const column = mapping[field];
      return column === undefined ? '' : (file.rows[index].cells[column] ?? '');
    };
    return validation.results.flatMap((result, index) => {
      const invalid = result.errors.length > 0;
      if (onlyErrors && !invalid && !touched.has(index)) return [];
      return [
        {
          index,
          rowNumber: file.rows[index].rowNumber,
          values: {
            name: cell(index, 'name'),
            launchDate: cell(index, 'launchDate'),
            launchTime: cell(index, 'launchTime'),
            notificationTitle: cell(index, 'notificationTitle'),
            notificationBody: cell(index, 'notificationBody'),
            notificationLink: cell(index, 'notificationLink'),
            utmCampaign: cell(index, 'utmCampaign'),
            labels: cell(index, 'labels'),
          },
          defaults: {
            notificationLink: result.row.notificationLink,
            utmCampaign: result.row.utmCampaign,
          },
          errors: result.errors,
        },
      ];
    });
  }, [file, validation, edits, mapping, onlyErrors, touched]);

  if (!file || !validation) return null;

  const canContinue =
    counts.valid > 0 && (counts.invalid === 0 || state.skipInvalid);

  const downloadErrors = () => {
    const invalidRows = validation.results.flatMap((result, index) =>
      result.errors.length > 0
        ? [
            {
              rowNumber: file.rows[index].rowNumber,
              cells: file.rows[index].cells,
              errors: result.errors,
            },
          ]
        : []
    );
    downloadCsvFile(
      buildErrorsCsv({
        headers: file.headers,
        rows: invalidRows,
        rowNumberHeader: LL.campaignImport.review.rowNumberHeader(),
        errorsHeader: LL.campaignImport.review.errorsHeader(),
        describeError: messages.describe,
      }),
      LL.campaignImport.review.errorsCsvFileName()
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <StatTile
          label={LL.campaignImport.review.validLabel()}
          value={counts.valid}
          tone="good"
        />
        <StatTile
          label={LL.campaignImport.review.invalidLabel()}
          value={counts.invalid}
          tone="bad"
        />
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {LL.campaignImport.review.summaryAnnouncement({
          valid: counts.valid,
          invalid: counts.invalid,
        })}
      </p>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <Switch
            id="import-only-errors"
            checked={onlyErrors}
            onCheckedChange={(checked) => {
              setOnlyErrors(checked);
              setTouched(new Set());
            }}
          />
          <Label htmlFor="import-only-errors" className="text-white/85">
            {LL.campaignImport.review.onlyErrors()}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="import-skip-invalid"
            checked={state.skipInvalid}
            onCheckedChange={(value) =>
              dispatch({ type: 'skipInvalidChanged', value })
            }
          />
          <Label htmlFor="import-skip-invalid" className="text-white/85">
            {LL.campaignImport.review.skipInvalid()}
          </Label>
        </div>
        {state.skipInvalid ? (
          <p className="text-sm text-white/70" data-slot="skipped-count">
            {LL.campaignImport.review.skippedCount({ count: counts.invalid })}
          </p>
        ) : null}
        {counts.invalid > 0 ? (
          <Button
            type="button"
            variant="outline"
            onClick={downloadErrors}
            className="ml-auto border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            <Download aria-hidden />
            {LL.campaignImport.review.downloadErrors()}
          </Button>
        ) : null}
      </div>

      {gridRows.length > 0 ? (
        <ReviewGrid
          rows={gridRows}
          hasTimeColumn={mapping.launchTime !== undefined}
          storeWebsiteDomain={state.storeWebsiteDomain}
          onEdit={(rowIndex, field, value) => {
            setTouched((previous) => new Set(previous).add(rowIndex));
            dispatch({
              type: 'cellEdited',
              rowIndex,
              field,
              value,
              now: DateTime.now(),
            });
          }}
        />
      ) : (
        <p className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-6 text-center text-sm text-white/70">
          {LL.campaignImport.review.noRowsToShow()}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-amber-200" role="status" aria-live="polite">
          {counts.valid === 0
            ? LL.campaignImport.review.noValidRows()
            : canContinue
              ? ''
              : LL.campaignImport.review.continueBlocked()}
        </p>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => dispatch({ type: 'goBack' })}
            className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            {LL.campaignImport.review.back()}
          </Button>
          <Button
            type="button"
            disabled={!canContinue}
            onClick={() => dispatch({ type: 'advanceToConfirm' })}
            className="bg-[#155DFC] text-white hover:bg-[#155DFC]/90"
          >
            {LL.campaignImport.review.continue()}
          </Button>
        </div>
      </div>
    </div>
  );
}
